import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Badge } from "@/components/common";
import { Loader2, CheckCircle } from "lucide-react";

const ROLE_LABEL = {
  admin: "Admin", recruiter: "Recruiter", research_analyst: "Research Analyst",
};

export default function ProfilePage() {
  const { user } = useAuth();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setSuccess("");
    if (newPwd.length < 4) return setError("Password must be at least 4 characters.");
    if (newPwd !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      setSuccess("Password updated successfully.");
      setOldPwd(""); setNewPwd(""); setConfirm("");
    } catch (e2) { setError(formatErr(e2)); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <PageHeader testId="profile-header" title="Profile" subtitle="Manage your account and security." />

      <div className="card-base p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-brand-light text-brand grid place-items-center font-heading font-bold text-xl">
            {user.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <div className="font-heading text-xl font-bold">{user.name}</div>
            <div className="text-sm text-gray-500">{user.email}</div>
            <div className="mt-1"><Badge variant="brand">{ROLE_LABEL[user.role] || user.role}</Badge></div>
          </div>
        </div>
      </div>

      <div className="card-base p-6">
        <h3 className="font-heading text-lg font-bold mb-1">Change Password</h3>
        <p className="text-sm text-gray-500 mb-6">Use a strong password you don't reuse anywhere else.</p>
        <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="change-password-form">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
          {success && (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2">
              <CheckCircle size={14} /> {success}
            </div>
          )}
          <label className="block">
            <span className="label-base">Current Password</span>
            <input type="password" required value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} className="input-base" data-testid="old-password-input" />
          </label>
          <label className="block">
            <span className="label-base">New Password</span>
            <input type="password" required value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="input-base" data-testid="new-password-input" />
          </label>
          <label className="block">
            <span className="label-base">Confirm New Password</span>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-base" data-testid="confirm-password-input" />
          </label>
          <button type="submit" className="btn-primary" disabled={loading} data-testid="change-password-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
